import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';
import { LanguageSwitcher } from '../components/layout/LanguageSwitcher';
import { track } from '../lib/analytics';
import { apiErrorMessage } from '../lib/errors';

type Mode = 'signin' | 'signup';

export function Auth() {
  const { t } = useI18n();
  const { status, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get('mode') === 'signup' ? 'signup' : 'signin');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const from = useMemo(() => (location.state as { from?: string } | null)?.from ?? '/studio', [location.state]);

  // Already signed in → skip the form.
  useEffect(() => {
    if (status === 'authenticated') navigate(from, { replace: true });
  }, [status, from, navigate]);

  function validate(): boolean {
    const next: typeof errors = {};
    if (mode === 'signup' && name.trim().length === 0) next.name = t.errors.fieldRequired;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) next.email = t.errors.badEmail;
    if (password.length < 8) next.password = t.errors.shortPassword;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setErrors((prev) => ({ ...prev, form: undefined }));
    try {
      if (mode === 'signup') {
        await signUp(email, password, name.trim());
        track('signup_completed', {});
        // New creators go through onboarding first (F-054): pick a handle +
        // guided first upload. `from` is carried so "Go to studio" can honour
        // any originally-requested destination.
        navigate('/onboarding', { replace: true, state: { from } });
        return;
      }
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setErrors((prev) => ({ ...prev, form: apiErrorMessage(err, t) }));
    } finally {
      setSubmitting(false);
    }
  }

  const isSignup = mode === 'signup';

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="mx-auto flex w-full max-w-content items-center justify-between px-4 py-4 sm:px-6">
        <Link to="/" className="type-heading-2 text-primary focus-ring rounded-sm">
          {t.common.brand}
        </Link>
        <LanguageSwitcher />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          <h1 className="type-display-lg text-primary">{isSignup ? t.auth.signUpTitle : t.auth.signInTitle}</h1>
          <p className="type-body text-secondary mt-1">{isSignup ? t.auth.signUpSubtitle : t.auth.signInSubtitle}</p>

          <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            {isSignup ? (
              <TextField
                label={t.auth.nameLabel}
                placeholder={t.auth.namePlaceholder}
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={errors.name}
                required
              />
            ) : null}
            <TextField
              label={t.auth.emailLabel}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              required
            />
            <TextField
              label={t.auth.passwordLabel}
              type="password"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              help={isSignup ? t.auth.passwordHint : undefined}
              required
            />

            {errors.form ? (
              <p className="type-body-sm text-danger" role="alert">
                {errors.form}
              </p>
            ) : null}

            <Button type="submit" size="lg" block loading={submitting}>
              {submitting ? t.auth.working : isSignup ? t.auth.signUpCta : t.auth.signInCta}
            </Button>
          </form>

          <button
            type="button"
            className="type-label text-secondary hover:text-primary focus-ring mt-6 rounded-sm"
            onClick={() => {
              setMode(isSignup ? 'signin' : 'signup');
              setErrors({});
            }}
          >
            {isSignup ? t.auth.toSignIn : t.auth.toSignUp}
          </button>
        </div>
      </main>
    </div>
  );
}
